import { useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { getRenewalReport, getUtilizationReport } from '../../services/api';
import DateRangePicker, { useDateRangePresets } from '../common/DateRangePicker';
import StatusBadge from '../common/StatusBadge';
import HelpTooltip from '../common/HelpTooltip';
import { Subscription } from '../../types';
import { format, parseISO, addDays } from 'date-fns';
import {
  CalendarDays,
  BarChart3,
  Download,
} from 'lucide-react';

type ReportType = 'renewals' | 'utilization';

export default function ReportsPage() {
  const [activeReport, setActiveReport] = useState<ReportType>('renewals');
  const presets = useDateRangePresets();

  // Renewal Report State
  const [renewalFromDate, setRenewalFromDate] = useState(
    format(new Date(), 'yyyy-MM-dd')
  );
  const [renewalToDate, setRenewalToDate] = useState(
    format(addDays(new Date(), 30), 'yyyy-MM-dd')
  );

  // Utilization Report State
  const [maxUtilization, setMaxUtilization] = useState<number | undefined>(undefined);

  // Fetch Renewal Report
  const fetchRenewalReport = useCallback(
    () => getRenewalReport(renewalFromDate, renewalToDate),
    [renewalFromDate, renewalToDate]
  );
  const {
    data: renewalData,
    loading: renewalLoading,
    error: renewalError,
  } = useApi(fetchRenewalReport, [renewalFromDate, renewalToDate]);

  // Fetch Utilization Report
  const fetchUtilizationReport = useCallback(
    () => getUtilizationReport(maxUtilization),
    [maxUtilization]
  );
  const {
    data: utilizationData,
    loading: utilizationLoading,
    error: utilizationError,
  } = useApi(fetchUtilizationReport, [maxUtilization]);

  const exportToCSV = (data: unknown[], filename: string) => {
    if (!data || data.length === 0) return;

    const headers = Object.keys(data[0] as object);
    const csvContent = [
      headers.join(','),
      ...data.map((row) =>
        headers
          .map((header) => {
            const value = (row as Record<string, unknown>)[header];
            const stringValue = String(value ?? '');
            return stringValue.includes(',') ? `"${stringValue}"` : stringValue;
          })
          .join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold text-foreground">
            Reports
          </h1>
          <HelpTooltip
            text="Generate reports to analyze your licensing data. Renewal Report shows upcoming renewals by date range. Utilization Report shows license usage across customers. Export to CSV for further analysis."
            
          />
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Generate and export licensing reports
        </p>
      </div>

      {/* Report Type Tabs */}
      <div className="card">
        <div className="border-b border-border">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveReport('renewals')}
              className={`px-6 py-4 text-sm font-medium border-b-2 ${
                activeReport === 'renewals'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <CalendarDays className="h-5 w-5 inline mr-2" />
              Renewal Report
              <HelpTooltip
                text="Shows all subscriptions renewing within a date range. Use this to plan renewals, notify customers, and verify auto-renew settings."
                size="sm"
                className="ml-1"
              />
            </button>
            <button
              onClick={() => setActiveReport('utilization')}
              className={`px-6 py-4 text-sm font-medium border-b-2 ${
                activeReport === 'utilization'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <BarChart3 className="h-5 w-5 inline mr-2" />
              Utilization Report
              <HelpTooltip
                text="Shows license usage (assigned vs purchased) per subscription. Low utilization means the customer is paying for unused licenses. Requires GDAP sync to populate data."
                size="sm"
                className="ml-1"
              />
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeReport === 'renewals' && (
            <div className="space-y-6">
              {/* Renewal Report Filters */}
              <div className="flex flex-col lg:flex-row lg:items-end gap-4">
                <div className="flex-1">
                  <DateRangePicker
                    fromDate={renewalFromDate}
                    toDate={renewalToDate}
                    onFromDateChange={setRenewalFromDate}
                    onToDateChange={setRenewalToDate}
                    label="Renewal Date Range"
                  />
                </div>
                <div className="flex space-x-2">
                  {presets.slice(0, 4).map((preset) => (
                    <button
                      key={preset.label}
                      onClick={() => {
                        const range = preset.getRange();
                        setRenewalFromDate(range.fromDate);
                        setRenewalToDate(range.toDate);
                      }}
                      className="px-3 py-2 text-xs rounded bg-muted text-muted-foreground hover:bg-accent"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => exportToCSV(renewalData?.items || [], 'renewal_report')}
                  disabled={!renewalData?.items?.length}
                  className="btn-primary"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </button>
              </div>

              {/* Renewal Report Results */}
              {renewalLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : renewalError ? (
                <div className="text-red-600 dark:text-red-400">{renewalError}</div>
              ) : (
                <>
                  <div className="text-sm text-muted-foreground">
                    {renewalData?.totalCount || 0} subscriptions renewing between{' '}
                    {format(parseISO(renewalFromDate), 'MMM d, yyyy')} and{' '}
                    {format(parseISO(renewalToDate), 'MMM d, yyyy')}
                  </div>

                  <div className="overflow-x-auto">
                    <table className="table">
                      <thead className="bg-muted">
                        <tr>
                          <th className="w-48">Client</th>
                          <th className="w-64">Product</th>
                          <th className="w-16 text-center">Qty</th>
                          <th className="w-32">Renewal Date</th>
                          <th className="w-28">Status</th>
                          <th className="w-28 text-right">Total Price</th>
                        </tr>
                      </thead>
                      <tbody className="bg-card divide-y divide-border">
                        {renewalData?.items?.map((sub: Subscription & { clientName?: string }) => (
                          <tr key={sub.id}>
                            <td className="font-medium truncate max-w-[12rem]" title={sub.clientName}>{sub.clientName}</td>
                            <td className="truncate max-w-[16rem]" title={sub.productName}>{sub.productName}</td>
                            <td className="text-center">{sub.quantity}</td>
                            <td>{sub.renewalDate ? format(parseISO(sub.renewalDate), 'MMM d, yyyy') : 'N/A'}</td>
                            <td>
                              <StatusBadge status={sub.status} type="subscription" />
                            </td>
                            <td className="text-right">${sub.totalPrice.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {activeReport === 'utilization' && (
            <div className="space-y-6">
              {/* Utilization Report Filters */}
              <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                <div>
                  <label className="label">Max Utilization %</label>
                  <select
                    value={maxUtilization || ''}
                    onChange={(e) =>
                      setMaxUtilization(e.target.value ? parseInt(e.target.value) : undefined)
                    }
                    className="input"
                  >
                    <option value="">All</option>
                    <option value="25">Under 25%</option>
                    <option value="50">Under 50%</option>
                    <option value="75">Under 75%</option>
                  </select>
                </div>
                <button
                  onClick={() => exportToCSV(utilizationData?.items || [], 'utilization_report')}
                  disabled={!utilizationData?.items?.length}
                  className="btn-primary"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </button>
              </div>

              {/* Utilization Report Results */}
              {utilizationLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : utilizationError ? (
                <div className="text-red-600 dark:text-red-400">{utilizationError}</div>
              ) : utilizationData?.items?.length === 0 ? (
                <div className="text-center py-12">
                  <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    No Utilization Data Available
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-md mx-auto">
                    Utilization data requires running the GDAP sync to fetch license assignment information
                    from Microsoft 365 tenants. Go to the Dashboard and click "Sync GDAP" to populate this data.
                  </p>
                </div>
              ) : (
                <>
                  <div className="text-sm text-muted-foreground">
                    {utilizationData?.totalCount || 0} subscriptions with utilization data
                  </div>

                  <div className="overflow-x-auto">
                    <table className="table">
                      <thead className="bg-muted">
                        <tr>
                          <th className="w-48">Client</th>
                          <th className="w-64">Product</th>
                          <th className="w-20 text-center">Licenses</th>
                          <th className="w-20 text-center">Used</th>
                          <th className="w-32">Utilization</th>
                          <th className="w-28">Renewal</th>
                        </tr>
                      </thead>
                      <tbody className="bg-card divide-y divide-border">
                        {utilizationData?.items?.map(
                          (sub: Subscription & { clientName?: string; utilizationPercent: number }) => (
                            <tr key={sub.id}>
                              <td className="font-medium truncate max-w-[12rem]" title={sub.clientName}>{sub.clientName}</td>
                              <td className="truncate max-w-[16rem]" title={sub.productName}>{sub.productName}</td>
                              <td className="text-center">{sub.quantity}</td>
                              <td className="text-center">{sub.assignedQuantity ?? '-'}</td>
                              <td>
                                <div className="flex items-center space-x-2">
                                  <div className="w-16 bg-muted rounded-full h-2">
                                    <div
                                      className={`h-2 rounded-full ${
                                        sub.utilizationPercent < 50
                                          ? 'bg-yellow-500'
                                          : 'bg-green-500'
                                      }`}
                                      style={{ width: `${Math.min(sub.utilizationPercent, 100)}%` }}
                                    />
                                  </div>
                                  <span className="text-sm">{sub.utilizationPercent}%</span>
                                </div>
                              </td>
                              <td>{sub.renewalDate ? format(parseISO(sub.renewalDate), 'MMM d, yyyy') : 'N/A'}</td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
